import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";

import {
  IQuickReplyTemplate,
  IQuickReplyTemplateData,
  IQuickReplyTemplatePayload,
  TemplateForType,
} from "./quickReplyTemplate.types";

// Action Types
export const FETCH_QUICK_REPLY_TEMPLATE_LIST_PROGRESS =
  "FETCH_QUICK_REPLY_TEMPLATE_LIST_PROGRESS";
export const FETCH_QUICK_REPLY_TEMPLATE_LIST_SUCCESS =
  "FETCH_QUICK_REPLY_TEMPLATE_LIST_SUCCESS";
export const FETCH_QUICK_REPLY_TEMPLATE_LIST_FAILED =
  "FETCH_QUICK_REPLY_TEMPLATE_LIST_FAILED";

// Action Creators
export const fetchQuickReplyTemplateListProgress = () =>
  action(FETCH_QUICK_REPLY_TEMPLATE_LIST_PROGRESS);
export const fetchQuickReplyTemplateListSuccess = (
  data: IQuickReplyTemplate[],
  totalRecords: number
) => action(FETCH_QUICK_REPLY_TEMPLATE_LIST_SUCCESS, { data, totalRecords });
export const fetchQuickReplyTemplateListFailed = (errorMessage: string) =>
  action(FETCH_QUICK_REPLY_TEMPLATE_LIST_FAILED);

// Async Actions - Matching fetchMarketingQuickReplyTemplatesListAsync pattern
export const fetchQuickReplyTemplateListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: { fromDate: string; toDate: string },
    searchValue: any[], // IDatatableV2AdvancedSearchFilter
    company_outlet_uuid?: string,
    searchByName?: string,
    searchByTemplateId?: string,
    template_for?: TemplateForType,
    onCallBack?: (data: IQuickReplyTemplate[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchQuickReplyTemplateListProgress());
      dispatch(saveLoaderProgress());

      let url = "";

      if (status !== "-1") {
        url += `&status=${status}`;
      }

      if (template_for) {
        url += `&template_for=${template_for}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      if (searchByName) {
        url += `&columns=${JSON.stringify(["name"])}&value=${searchByName}`;
      }

      if (searchValue.length > 0 || searchByTemplateId) {
        const advanceFilter = [...searchValue];

        if (searchByTemplateId) {
          advanceFilter.push({
            column: ["template_id"],
            value: searchByTemplateId,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (date) {
        url += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      const finalUrl =
        `/conversation/get-quick-reply?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}` +
        url;

      const res = await api.get(finalUrl);

      const data: IQuickReplyTemplate[] = res.data.data;
      const totalRecords = res.data.totalRecords || 0;

      dispatch(fetchQuickReplyTemplateListSuccess(data, totalRecords));

      // Execute callback if provided
      if (onCallBack) {
        onCallBack(data);
      }
    } catch (err: any) {
      dispatch(fetchQuickReplyTemplateListFailed(err?.response?.data?.message));
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_QUICK_REPLY_TEMPLATE_PROGRESS =
  "FETCH_QUICK_REPLY_TEMPLATE_PROGRESS";
export const FETCH_QUICK_REPLY_TEMPLATE_SUCCESS =
  "FETCH_QUICK_REPLY_TEMPLATE_SUCCESS";
export const FETCH_QUICK_REPLY_TEMPLATE_FAILED =
  "FETCH_QUICK_REPLY_TEMPLATE_FAILED";

export const fetchQuickReplyTemplateProgress = () =>
  action(FETCH_QUICK_REPLY_TEMPLATE_PROGRESS);
export const fetchQuickReplyTemplateSuccess = (data: IQuickReplyTemplateData) =>
  action(FETCH_QUICK_REPLY_TEMPLATE_SUCCESS, { data });
export const fetchQuickReplyTemplateFailed = (errorMessage: string) =>
  action(FETCH_QUICK_REPLY_TEMPLATE_FAILED, { errorMessage });

export const fetchQuickReplyTemplateAsync =
  (
    templateName?: string,
    companyOutletUuid?: string,
    templateUuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchQuickReplyTemplateProgress());

      let url = "";
      if (templateName) {
        url += `&name=${templateName}`;
      }

      if (templateUuid) {
        url += `&quick_reply_uuid=${templateUuid}`;
      }
      const res = await api.get(
        `/conversation/get-quick-reply?&company_outlet_uuid=${companyOutletUuid}${url}`
      );
      const data = res.data.data;

      if (data.length > 0) {
        const finalData: IQuickReplyTemplateData = data[0];

        const result: any = {
          components: [],
          header: {
            type: "HEADER",
            text: "",
            example: {
              header_text: [[]],
            },
          },
          body: {
            type: "BODY",
            text: "",
            example: {
              body_text: [[]],
            },
          },
          buttons: {
            type: "BUTTONS",
            buttons: [],
          },
          footer: {
            type: "FOOTER",
            text: "",
          },
        };

        for (const data of finalData.components) {
          if (data.type === "HEADER") {
            result.header.text = data.text;
            result.header.example = data.example;
          } else if (data.type === "BODY") {
            result.body.text = data.text;
          } else if (data.type === "BUTTONS") {
            result.buttons.buttons = data.buttons as any;
          } else if (data.type === "FOOTER") {
            result.footer.text = data.text;
          }
        }
        finalData.header = result.header;
        finalData.body = result.body;
        finalData.footer = result.footer;
        finalData.buttons = result.buttons;
        finalData.example = {
          body_text:
            result.body.example &&
            result.body.example.body_text &&
            result.body.example.body_text.length > 0
              ? result.body.example.body_text[0]
              : [],
          header_text:
            result.header.example &&
            result.header.example.header_text &&
            result.header.example.header_text.length > 0
              ? result.header.example.header_text[0]
              : [],
        };

        dispatch(fetchQuickReplyTemplateSuccess(finalData));
      } else {
        dispatch(
          fetchQuickReplyTemplateFailed(
            "Unfortunately, there are no records available at the moment."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchQuickReplyTemplateFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertQuickReplyTemplate =
  (
    data: IQuickReplyTemplateData,
    onCallBack: (
      isSuccess?: boolean,
      templateData?: IQuickReplyTemplateData
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const finalData: IQuickReplyTemplatePayload = {
        quick_reply_uuid: data.quick_reply_uuid || null,
        name: data.name.toLowerCase(),
        text: data.text,
        company_outlet_uuid: data.company_outlet_uuid,
        status: data.status,
      };

      const res = await api.post("/conversation/upsert-quick-reply", finalData);

      const templateData = res.data.data;
      dispatch(
        showMessage({
          type: "success",
          message:
            data.status === "INACTIVE"
              ? "Quick reply template is deleted successfully"
              : data.quick_reply_uuid
              ? "Quick reply template is updated successfully"
              : "Quick reply template is created successfully",
          displayAs: "snackbar",
        })
      );
      onCallBack(true, templateData);
    } catch (err: any) {
      onCallBack(false, undefined);
      dispatch(
        showMessage({
          type: "error",
          message:
            JSON.stringify(err?.response?.data) ||
            "Something went to be wrong!",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const deleteQuickReplyTemplate =
  (
    company_outlet_uuid: string,
    name: string,
    onCallBack: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      await api.post(`/conversation/delete-quick-reply`, {
        company_outlet_uuid,
        name,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Quick reply template is deleted successfully",
          displayAs: "snackbar",
        })
      );
      onCallBack(true);
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message: JSON.stringify(err.response.data),
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_QUICK_REPLY_TEMPLATE_STATE =
  "CLEAR_QUICK_REPLY_TEMPLATE_STATE";
export const CLEAR_QUICK_REPLY_TEMPLATE = "CLEAR_QUICK_REPLY_TEMPLATE";

export const clearQuickReplyTemplateState = () =>
  action(CLEAR_QUICK_REPLY_TEMPLATE_STATE);
export const clearQuickReplyTemplate = () => action(CLEAR_QUICK_REPLY_TEMPLATE);
